import {UIModuleV2} from '@gongxigames/framework/slotV2/baseClasses/uiModuleV2/UiModuleV2';
import {LAYERS} from '@gongxigames/framework/slot/configs/Layers';
import {Store, Signal} from '@gongxigames/framework/core/fragments/store';
import {$SpinBtnUi, SpinBtnCurrentAction, SpinBtnMode} from './components/SpinBtnUi';
import {Container} from 'pixi.js';
import {CoreDI, inject, SlotSettings} from '@gongxigames/framework/slot';
import {CounterData} from '@gongxigames/framework/promotions';
import {slotStore} from '@gongxigames/framework/slotV2/globals';
import {HOLD_FOR_ACTION_CALL_TIMER, SpinBtnAnimationType} from '@gongxigames/framework/slotV2/ui/ui-koala/modules/SpinButton/components/SpinBtn';
import {Timer} from '@gongxigames/framework/core';
import {specialInfoZoneStore} from '@gongxigames/framework/slotV2/services/SpecialInfoZone/specialInfoZoneStore';

export class SpinButtonModule extends UIModuleV2 {
    public static NAME = 'SpinButtonModule';
    public static layer = LAYERS.KEYPAD;
    protected readonly HOLD_FOR_TURBO_LABEL_DELAY = 3000;

    protected currentBtnAction: Signal<SpinBtnCurrentAction> = Store.signal('spin');
    protected mode: Signal<SpinBtnMode> = Store.signal('base');
    protected disabled = Store.signal(false);
    protected btnOuterAnimation: Signal<SpinBtnAnimationType> = Store.signal('hideBtnOuter');
    protected autoplayCounter = Store.computed(() => String(slotStore.autoplayOptions.value?.autoplaySpins || 0));
    protected fsCurrent = Store.signal(100);
    protected fsTotal = Store.signal(100);
    protected state: 'skip'|'spin'| 'quickStop' = 'spin';
    protected holdForTurboLabelTimer?: Timer;
    protected holdForActionKeyboardTimer?: Timer;

    // TODO convert to signals/move to store
    @inject(CoreDI.Settings)
    protected _settings!: SlotSettings;

    public container = new Container();

    onInit(): void {
        const buttonOffset = Store.computed(() => {
            const {blackPanelHeight, numberOfRows, position} = specialInfoZoneStore.values();

            return (slotStore.stage_isLandscape.value || position === 'top') ? 0 : blackPanelHeight * numberOfRows;
        });

        const uiDefault = $SpinBtnUi({
            buttonOffset,
            currentBtnAction: this.currentBtnAction,
            mode: this.mode,
            disabled: this.disabled,
            autoplayCounter: this.autoplayCounter,
            action: this.onSpin.bind(this),
            pointerOutAction: this.onPointerOut.bind(this),
            holdForAction: this.isTurboModeDisabled ? undefined : this.holdForAction.bind(this),
            fsCurrent: this.fsCurrent,
            fsTotal: this.fsTotal,
            btnOuterAnimation: this.btnOuterAnimation,
        }) as Container;

        this.container.addChild(uiDefault);
    }

    protected holdForAction(): void {
        // TODO different for koala and ruby
        // for ruby - start autoplay, for koala - start holdForTurbo mode
        this.hideBtnOuter();
        this.emitter.emit('UI.holdSpinForAction.started') ;
    }

    protected onSpin(): void {
        // TODO different for koala and ruby
        // for ruby - stop autoplay, for koala - stop current spin
        slotStore.soundPlayer.value?.play('spinclick');
        switch (this.state) {
            case 'spin':
                this.emitter.emit('UI.spinButton.pressed');
                break;
            case 'skip':
                this.emitter.emit('UI.skipButton.pressed');
                this.disableButton();
                break;
            case 'quickStop':
                this.emitter.emit('UI.quickStopButton.pressed');
                this.disableButton();
                break;
            default:
                break;
        }
    }

    protected onPointerOut(): void {
        this.emitter.emit('UI.spinButton.pointerout');
    }

    protected onSetupEvents(): void {
        this.emitter.on('UI.playTransitionToFreeSpins', this.initFreeSpins.bind(this));
        this.emitter.on('UI.playTransitionToMainGame', this.initMainGame.bind(this));
        this.emitter.on('UI.holdForTurboActivated', this.initHoldForTurboMode.bind(this));
        this.emitter.on('UI.holdForTurboDeactivated', this.deactivateHoldForTurboMode.bind(this));
        this.emitter.on('UI.enableSpinAutoMode', this.onAutoplayStarted.bind(this));
        this.emitter.on('UI.disableSpinAutoMode', this.onAutoplayStopped.bind(this));

        this.emitter.on('General.disableSpinButton', this.disableButton.bind(this));
        this.emitter.on('General.enableSpinButton', this.enableButton.bind(this));
        this.emitter.on('Integration.disableSpinButton', this.disableButton.bind(this));
        this.emitter.on('Integration.enableSpinButton', this.enableButton.bind(this));

        this.emitter.on('Game.command.quickStopEnable', this.enableQuickStopButton.bind(this));
        this.emitter.on('Game.command.skipEnable', this.enableSkipButton.bind(this));
        this.emitter.on('Game.command.skipDisable', this.disableSkipButton.bind(this));

        this.emitter.on('General.Game.STARTED', this.onGameStarted.bind(this));
        this.emitter.on('General.FreeGames.UpdateCounter', this.onUpdateFGCounter.bind(this));
        this.emitter.on('UI.startHoldForTurboLabelTimer', this.startHoldForTurboLabelTimer.bind(this));
    }

    protected enableButton(): void {
        this.state = 'spin';
        this.disabled.value = false;
    }

    protected enableSkipButton(): void {
        this.state = 'skip';
        this.disabled.value = false;
    }

    protected enableQuickStopButton(): void {
        if (this._settings.get('quickStopDisabled')) {
            return;
        }

        this.state = 'quickStop';
        this.disabled.value = false;
    }

    protected disableButton(): void {
        // TODO different for koala and ruby
        // if (this.mode.value !== 'autoplay') {
        //     this.disabled.value = true;
        // }
        this.disabled.value = true;

        if (this.mode.value === 'base') {
            this.hideBtnOuter();
        }
    }

    protected disableSkipButton(): void {
        if (this.mode.value !== 'hold_for_turbo_active') {
            this.disableButton();
        }
    }

    protected onGameStarted(): void {
        this.emitter.on('General.UI.SPACEBAR_UP', this.onSpaceBarUp.bind(this));
        this.emitter.on('General.UI.SPACEBAR_DOWN', this.onSpaceBarDown.bind(this));
    }

    protected initFreeSpins(): void {
        this.mode.value = 'free_spins';
        this.showBtnOuterImmediately();
    }

    protected initMainGame(): void {
        this.mode.value = 'base';
        this.hideBtnOuter();
    }

    protected initHoldForTurboMode(): void {
        this.mode.value = 'hold_for_turbo_active';
    }

    protected deactivateHoldForTurboMode(): void {
        this.initMainGame();
    }

    protected onSpaceBarDown(): void {
        if (!this.disabled.value && this.mode.value === 'base' && !this.isTurboModeDisabled) {
            this.holdForActionKeyboardTimer = slotStore.updateManager.value!.addTimer(() => {
                this.holdForAction();
                this.holdForActionKeyboardTimer = undefined;
            }, HOLD_FOR_ACTION_CALL_TIMER, true);
        }
    }

    protected onSpaceBarUp(): void {
        this.holdForActionKeyboardTimer?.stop();
        this.holdForActionKeyboardTimer = undefined;

        if (!this.disabled.value) {
            this.onSpin();
        }
    }

    protected onAutoplayStarted(): void {
        this.mode.value = 'autoplay';
        this.showBtnOuterImmediately();
        this.enableButton();
    }

    protected onAutoplayStopped(): void {
        this.mode.value = 'base';
    }

    protected onUpdateFGCounter({left, total}: CounterData): void {
        this.fsCurrent.value = left;
        this.fsTotal.value = total ?? 0;
    }

    protected startHoldForTurboLabelTimer(): void {
        if (this.isTurboModeDisabled) {
            return;
        }
        this.hideBtnOuter();
        this.holdForTurboLabelTimer = slotStore.updateManager.value!.addTimer(() => {
            this.btnOuterAnimation.value = 'showBtnOuterTween';
        }, this.HOLD_FOR_TURBO_LABEL_DELAY, true);
    }

    protected showBtnOuterImmediately(): void {
        if (this.isTurboModeDisabled) {
            return;
        }
        this.btnOuterAnimation.value = 'showBtnOuter';
    }

    private hideBtnOuter() {
        if (this.isTurboModeDisabled) {
            return;
        }

        if (this.holdForTurboLabelTimer && this.holdForTurboLabelTimer.isPlaying) {
            this.holdForTurboLabelTimer.stop();
        }

        this.btnOuterAnimation.value = 'hideBtnOuterTween';
    }

    private get isTurboModeDisabled(): boolean {
        return this._settings.get('turboModeDisabled');
    }

    public destroy(): void {
        super.destroy();

        this.holdForActionKeyboardTimer?.stop();
        this.holdForActionKeyboardTimer = undefined;

        this.holdForTurboLabelTimer?.stop();
        this.holdForTurboLabelTimer = undefined;
    }
}